// "Find My Carrier Requirements" — instant lookup. No backend; pulls from RCF_CARRIERS.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Search, ArrowRight, AlertCircle, Phone } from "lucide-react";
import { RCF_CARRIERS, RCF_SERVICES } from "@/data/rcfCities";
import { COMPANY } from "@/lib/constants";

const CarrierFinder = () => {
  const [carrierSlug, setCarrierSlug] = useState<string>("");
  const [roofAge, setRoofAge] = useState<string>("");

  const carrier = useMemo(() => RCF_CARRIERS.find(c => c.slug === carrierSlug), [carrierSlug]);

  // Recommend services based on carrier requirements + roof age
  const recommended = useMemo(() => {
    if (!carrier) return [];
    const recs: { slug: string; reason: string }[] = [];
    recs.push({ slug: "roof-condition-certificate", reason: `Required by ${carrier.shortName} for new policies and renewals.` });
    recs.push({ slug: "wind-mitigation", reason: `Unlocks ${carrier.shortName}-compliant FL premium discounts (avg. $500–$1,200/yr).` });
    const age = parseInt(roofAge, 10);
    if (!isNaN(age) && age >= 25) {
      recs.push({ slug: "4-point-inspection", reason: `Roof is ${age} yrs old — ${carrier.shortName} requires 4-point for homes 25+ years.` });
    }
    if (!isNaN(age) && age >= 12) {
      recs.push({ slug: "roof-age-verification", reason: "Older roofs frequently trigger carrier age disputes — pre-empt with a verification letter." });
    }
    return recs.map(r => ({ ...r, service: RCF_SERVICES.find(s => s.slug === r.slug)! })).filter(r => r.service);
  }, [carrier, roofAge]);

  const faqs = useMemo(() => {
    if (!carrier) return [];
    return [
      { q: `Does ${carrier.shortName} require a roof certificate?`, a: `Yes. ${carrier.description}` },
      { q: `What inspection forms does ${carrier.shortName} accept?`, a: `${carrier.shortName} accepts the standard FL OIR-B1-1802 wind mitigation form and any state-licensed contractor's roof condition certification dated within 12 months.` },
      { q: `How fast can I get a ${carrier.shortName}-accepted report?`, a: `O'Brien Contracting (CCC1335947) issues ${carrier.shortName}-accepted reports within 24–48 hours of inspection statewide.` },
    ];
  }, [carrier]);

  return (
    <section className="py-12 bg-background border-y border-border" id="carrier-finder">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium mb-3">
            <Search className="w-4 h-4" /> Carrier Lookup
          </div>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">
            Find My Carrier Requirements
          </h2>
          <p className="text-muted-foreground">
            Pick your Florida insurer and roof age — see exactly what reports you need to keep your policy bound.
          </p>
        </div>

        <Card className="shadow-lg">
          <CardContent className="p-6">
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">Your insurance carrier *</label>
                <Select value={carrierSlug} onValueChange={setCarrierSlug}>
                  <SelectTrigger><SelectValue placeholder="Select carrier..." /></SelectTrigger>
                  <SelectContent>
                    {RCF_CARRIERS.map(c => (
                      <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">Roof age (years)</label>
                <Select value={roofAge} onValueChange={setRoofAge}>
                  <SelectTrigger><SelectValue placeholder="Select age..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">Under 10 years</SelectItem>
                    <SelectItem value="12">10–15 years</SelectItem>
                    <SelectItem value="18">16–20 years</SelectItem>
                    <SelectItem value="25">21–25 years</SelectItem>
                    <SelectItem value="30">26+ years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!carrier ? (
              <div className="text-center text-sm text-muted-foreground py-6">
                Choose your carrier to see requirements instantly.
              </div>
            ) : (
              <div className="space-y-6">
                {/* Requirements */}
                <div>
                  <h3 className="font-heading font-bold text-foreground mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                    {carrier.shortName} requires:
                  </h3>
                  <ul className="space-y-1.5 text-sm text-muted-foreground bg-muted/30 rounded-lg p-4">
                    {carrier.requirements.map(r => (
                      <li key={r} className="flex gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" /> {r}
                      </li>
                    ))}
                  </ul>
                  {carrier.notes && (
                    <p className="text-xs text-muted-foreground mt-2 flex gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {carrier.notes}
                    </p>
                  )}
                </div>

                {/* Recommended actions */}
                <div>
                  <h3 className="font-heading font-bold text-foreground mb-3">Your next steps:</h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {recommended.map(({ service, reason }) => (
                      <Card key={service.slug} className="border-primary/20">
                        <CardContent className="p-4">
                          <h4 className="font-bold text-foreground text-sm mb-1">{service.name}</h4>
                          <p className="text-xs text-muted-foreground mb-3">{reason}</p>
                          <Link to={`/carrier/${carrier.slug}#service-${service.slug}`}>
                            <Button size="sm" variant="outline" className="w-full">
                              See {carrier.shortName} requirements <ArrowRight className="w-3.5 h-3.5 ml-1" />
                            </Button>
                          </Link>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* FAQ */}
                <div>
                  <h3 className="font-heading font-bold text-foreground mb-3">{carrier.shortName} FAQs</h3>
                  <div className="space-y-3">
                    {faqs.map(f => (
                      <div key={f.q} className="border border-border rounded-lg p-3">
                        <p className="font-medium text-foreground text-sm mb-1">{f.q}</p>
                        <p className="text-sm text-muted-foreground">{f.a}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <div className="bg-primary text-primary-foreground rounded-lg p-5 flex flex-col sm:flex-row items-center justify-between gap-3">
                  <div>
                    <p className="font-bold">Ready to register your home?</p>
                    <p className="text-sm text-primary-foreground/80">Same-week scheduling. {carrier.shortName}-accepted reports in 24–48 hrs.</p>
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/carrier/${carrier.slug}#requirements`}>
                      <Button variant="accent" size="sm">Full {carrier.shortName} guide</Button>
                    </Link>
                    <a href={COMPANY.phoneHref}>
                      <Button variant="outline" size="sm" className="bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 gap-1">
                        <Phone className="w-3.5 h-3.5" /> Call
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default CarrierFinder;
